import { SpanStatusCode, trace } from "@opentelemetry/api";
import {
  defineMutator,
  type ReadonlyJSONValue,
  type Transaction,
} from "@rocicorp/zero";
import type { StandardSchemaV1 } from "@standard-schema/spec";
import type { Context } from "../authorization";
import type { Schema } from "../generatedDrizzleSchema";
import { isUserFacingError } from "../userFacingError";
import {
  type AnyRbacResourceLookup,
  collectStrippedFromKeys,
  type DeclarativeResolve,
  type FnArgs,
  type PermissionRequirement,
  permissionRequirements,
  type PermissionsDeclaration,
  runMutation,
  validateResolveConfig,
} from "./resolve";

declare const brand: unique symbol;

/**
 * Branded mutator type. The phantom `brand` property makes this
 * incompatible with a bare `defineMutator()` return — so the
 * `satisfies Record<string, AppMutation>` in `index.ts` will
 * reject any mutation that skips the `mutator()` wrapper.
 */
export type InteractionType =
  | "create"
  | "update"
  | "delete"
  | "view"
  | "run"
  | "send"
  | "sync"
  | "review";

export type AppMutation = {
  readonly [brand]: true;
  redactKeys?: string[];
  redactKeysOnSecret?: string[];
  stripKeys?: Record<string, string[]>;
  description?: string;
  interactionType?: InteractionType;
};

/**
 * Builds a Zero shared mutator with typed argument resolution.
 *
 * Lifecycle:
 *
 *   1. Schema validation (Zero / `defineMutator`).
 *   2. `resolve` — turn raw `args` into domain objects by ID lookup.
 *   3. RBAC permission checks (`assertAccess` for each declared permission).
 *   4. `fn` — the mutation body. Its `args` are the parsed input merged
 *      with the resolver outputs, so the body can't be reached without
 *      first passing the access checks declared up top.
 *
 * Mutators that read or write no space-scoped resource (e.g.
 * `setUserPreference`) pass `resolve: {}` — the empty object is a
 * deliberately visible marker for code review.
 *
 * `permissions` is required: either a non-empty list of requirements, or
 * `{ none: "reason" }` when access is intentionally open — typically
 * because the body only reads and writes rows owned by `ctx.userId`.
 *
 * `redactKeys` / `stripKeys` control audit-log redaction for fields
 * containing secrets or large blobs (see `services/api/auditLogs/AGENTS.md`).
 */
export function mutator<
  TInput,
  TOutput extends Record<string, unknown> = TInput extends Record<
    string,
    unknown
  >
    ? TInput
    : Record<string, never>,
  TDecl extends DeclarativeResolve<TOutput> = Record<never, never>,
>(config: {
  schema: StandardSchemaV1<TInput, TOutput>;
  resolve: TDecl;
  permissions: PermissionsDeclaration<FnArgs<TOutput, TDecl>>;
  fn: (opts: {
    tx: Transaction<Schema>;
    ctx: Context;
    args: FnArgs<TOutput, TDecl>;
  }) => Promise<void>;
  redactKeys?: string[];
  redactKeysOnSecret?: string[];
  stripKeys?: Record<string, string[]>;
  description?: string;
  interactionType?: InteractionType;
}) {
  const decl = config.resolve as Record<string, AnyRbacResourceLookup>;
  validateResolveConfig(decl, "mutator");
  const stripped = collectStrippedFromKeys(decl);
  const requirements = permissionRequirements(config.permissions, "mutator");

  const def = defineMutator(
    config.schema as unknown as StandardSchemaV1<
      ToJSON<TInput>,
      ToJSON<TOutput>
    >,
    async (opts) => {
      const {
        tx,
        ctx,
        args: rawArgs,
      } = opts as {
        tx: Transaction<Schema>;
        ctx: Context;
        args: TOutput;
      };
      const args = rawArgs as Record<string, unknown>;

      let wrote = false;
      const isRebase = tx.reason === "rebase";
      const trackedTx = isRebase
        ? (new Proxy(tx as object, {
            get(target, prop) {
              if (prop === "mutate") wrote = true;
              const value = Reflect.get(target, prop);
              return typeof value === "function" ? value.bind(target) : value;
            },
          }) as Transaction<Schema>)
        : tx;

      try {
        await runMutation({
          tx: trackedTx,
          ctx,
          args,
          decl,
          stripped,
          permissions: requirements as readonly PermissionRequirement<
            Record<string, unknown>
          >[],
          fn: config.fn as (opts: {
            tx: Transaction<Schema>;
            ctx: Context;
            args: Record<string, unknown>;
          }) => Promise<void>,
        });
      } catch (err) {
        if (isRebase && !wrote && isUserFacingError(err)) return;
        throw err;
      }
    }
  );

  const extras: Record<string, unknown> = {
    // Exposed so server mutators can reference (not copy) the shared
    // declarations: `resolve: sharedMutatorDefs.x.resolve, permissions:
    // sharedMutatorDefs.x.permissions`. The defineMutators() registry
    // erases these from its types, hence the sharedMutatorDefs export.
    resolve: config.resolve,
    permissions: config.permissions,
  };
  if (config.redactKeys) extras.redactKeys = config.redactKeys;
  if (config.redactKeysOnSecret)
    extras.redactKeysOnSecret = config.redactKeysOnSecret;
  if (config.stripKeys) extras.stripKeys = config.stripKeys;
  if (config.description) extras.description = config.description;
  if (config.interactionType) extras.interactionType = config.interactionType;
  Object.assign(def as object, extras);

  return def as typeof def &
    AppMutation & {
      resolve: TDecl;
      permissions: PermissionsDeclaration<FnArgs<TOutput, TDecl>>;
    };
}

/**
 * Wraps every mutator's `fn` with a `mutator.<name>` span so each
 * mutation shows up as its own unit of work in Honeycomb on the server
 * and as a no-op trace on the client (when no web tracer is registered).
 * Exceptions are recorded on the span and rethrown; the caller's error
 * handling is unchanged. Uses only `@opentelemetry/api`, so it's safe to
 * import from UI-reachable code.
 */
export function traceMutators<T extends Record<string, unknown>>(defs: T): T {
  for (const [name, raw] of Object.entries(defs)) {
    const def = raw as { fn?: (opts: unknown) => Promise<unknown> };
    const original = def.fn;
    if (typeof original !== "function") continue;
    if ((original as { __traced?: boolean }).__traced) continue;
    const wrapped = async (opts: unknown) => {
      // Fetch the tracer per call so tests (and dynamic provider swaps)
      // always resolve to the currently registered global provider.
      const tracer = trace.getTracer("3b-mutator");
      return tracer.startActiveSpan(`mutator.${name}`, async (span) => {
        try {
          return await original(opts);
        } catch (err) {
          const e = err instanceof Error ? err : new Error(String(err));
          span.recordException(e);
          span.setStatus({ code: SpanStatusCode.ERROR, message: e.message });
          throw err;
        } finally {
          span.end();
        }
      });
    };
    (wrapped as { __traced?: boolean }).__traced = true;
    def.fn = wrapped;
  }
  return defs;
}

type ToJSON<T> = T extends ReadonlyJSONValue | undefined
  ? T
  : T & ReadonlyJSONValue;
